import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail } from '../../utils/commonUtils';
import { createShop, createShopFile, toShopPrimaryFileRequest, toShopRewardCardFileRequest } from '../../utils/shop';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';
import { FormData, FormErrors, AddressData, ShopFormUtils } from '../../utils/shopCommon';
import {
  UploadFilePayload,
  toUploadFilePayload,
  uploadSingleFileViaPresign,
} from '../../utils/storage';
import ShopImageUploadPreview from './components/ShopImageUploadPreview';
import { 
  Home,
  MapPin,
  Clock,
  Phone,
  Save,
  ArrowLeft,
  Image,
  Lock,
  Map as MapIcon,
  Eye,
  EyeOff,
  X,
  RefreshCw
} from 'lucide-react';

declare global {
  interface Window {
    naver: any;
  }
}

const StoreCreate: React.FC = () => {
  const toTopLevelRegion = (rawRegion?: string) => {
    if (!rawRegion) return '';
    const normalized = rawRegion.trim();
    const regionMap: Record<string, string> = {
      서울특별시: '서울',
      부산광역시: '부산',
      대구광역시: '대구',
      인천광역시: '인천',
      광주광역시: '광주',
      대전광역시: '대전',
      울산광역시: '울산',
      세종특별자치시: '세종',
      경기도: '경기',
      강원특별자치도: '강원',
      강원도: '강원',
      충청북도: '충북',
      충청남도: '충남',
      전북특별자치도: '전북',
      전라북도: '전북',
      전라남도: '전남',
      경상북도: '경북',
      경상남도: '경남',
      제주특별자치도: '제주',
      제주도: '제주'
    };

    return regionMap[normalized] || normalized.replace(/특별시|광역시|특별자치시|특별자치도|자치도|도|시/g, '');
  };

  // Router hooks
  const navigate = useNavigate();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('shop-list');
  const [showPassword, setShowPassword] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false);
  const [isNaverMapLoaded, setIsNaverMapLoaded] = useState(false);
  const [addressKeyword, setAddressKeyword] = useState('');
  const [addressResults, setAddressResults] = useState<AddressData[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [addressSearchError, setAddressSearchError] = useState<string | null>(null);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);

  // Form state
  const [formData, setFormData] = useState<FormData>(ShopFormUtils.getDefaultFormData());
  const [errors, setErrors] = useState<FormErrors>({});

  // API state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Brand state
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);

  const [primaryUploadFiles, setPrimaryUploadFiles] = useState<UploadFilePayload[]>([]);
  const [primaryImagePreview, setPrimaryImagePreview] = useState('');
  const [primaryImageError, setPrimaryImageError] = useState('');
  const [isPrimaryImageUploading, setIsPrimaryImageUploading] = useState(false);

  const [rewardCardUploadFiles, setRewardCardUploadFiles] = useState<UploadFilePayload[]>([]);
  const [rewardCardImagePreview, setRewardCardImagePreview] = useState('');
  const [rewardCardImageError, setRewardCardImageError] = useState('');
  const [isRewardCardImageUploading, setIsRewardCardImageUploading] = useState(false);

  const mapPreviewRef = React.useRef<HTMLDivElement | null>(null);
  const mapModalRef = React.useRef<HTMLDivElement | null>(null);
  const previewMapRef = React.useRef<any>(null);
  const previewMarkerRef = React.useRef<any>(null);
  const modalMapRef = React.useRef<any>(null);
  const modalMarkerRef = React.useRef<any>(null);

  // Check if screen is mobile size
  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 브랜드 목록 조회
  useEffect(() => {
    const fetchBrands = async () => {
      if (!projectDetail) {
        return;
      }

      try {
        setIsLoadingBrands(true);
        const projectCode = projectDetail?.code || getProjectCode();
        if (!projectCode) {
          console.log('프로젝트 코드가 없습니다.');
          return;
        }

        console.log('평탄화된 브랜드 목록 API 호출:', { projectCode });
        const response = await getBrandListFlat(projectCode);
        console.log('평탄화된 브랜드 목록 API 응답:', response);

        if (response && response.brands) {
          setBrands(response.brands);
          console.log('설정된 브랜드 목록:', response.brands);

          const defaultBrandCode = projectDetail.defaultBrandCode?.trim();
          if (
            defaultBrandCode &&
            response.brands.some((b) => b.code === defaultBrandCode)
          ) {
            setFormData((prev) =>
              prev.brandCode?.trim() ? prev : { ...prev, brandCode: defaultBrandCode }
            );
          }
        } else {
          console.warn('응답에 brands 데이터가 없습니다:', response);
          setBrands([]);
        }
      } catch (err) {
        console.error('브랜드 목록 조회 오류:', err);
        // 브랜드 목록 조회 실패는 에러로 표시하지 않고 빈 배열로 설정
        setBrands([]);
      } finally {
        setIsLoadingBrands(false);
      }
    };

    if (projectDetail) {
      fetchBrands();
    }
  }, [projectDetail]);

  useEffect(() => {
    const clientId = import.meta.env.VITE_NAVER_MAP_CLIENT_ID;
    if (!clientId) return;

    if (window.naver?.maps) {
      setIsNaverMapLoaded(true);
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-naver-map-script="true"]');
    if (existingScript) {
      const onLoad = () => setIsNaverMapLoaded(true);
      existingScript.addEventListener('load', onLoad);
      return () => existingScript.removeEventListener('load', onLoad);
    }

    const script = document.createElement('script');
    script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}&submodules=geocoder`;
    script.async = true;
    script.defer = true;
    script.dataset.naverMapScript = 'true';
    script.onload = () => setIsNaverMapLoaded(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!isNaverMapLoaded || !mapPreviewRef.current) return;

    const naver = window.naver;
    const lat = formData.lat || 37.5665;
    const lon = formData.lon || 126.978;
    const center = new naver.maps.LatLng(lat, lon);

    if (!previewMapRef.current) {
      previewMapRef.current = new naver.maps.Map(mapPreviewRef.current, { center, zoom: 16 });
    } else {
      previewMapRef.current.setCenter(center);
    }

    if (!previewMarkerRef.current) {
      previewMarkerRef.current = new naver.maps.Marker({ position: center, map: previewMapRef.current });
    } else {
      previewMarkerRef.current.setPosition(center);
    }
  }, [isNaverMapLoaded, formData.lat, formData.lon]);

  useEffect(() => {
    if (!isMapModalOpen || !isNaverMapLoaded || !mapModalRef.current) return;

    const naver = window.naver;
    const lat = formData.lat || 37.5665;
    const lon = formData.lon || 126.978;
    const center = new naver.maps.LatLng(lat, lon);

    modalMapRef.current = new naver.maps.Map(mapModalRef.current, { center, zoom: 16 });
    modalMarkerRef.current = new naver.maps.Marker({ position: center, map: modalMapRef.current });

    const clickListener = naver.maps.Event.addListener(modalMapRef.current, 'click', (e: any) => {
      const newLat = Number(e.coord.y.toFixed(6));
      const newLon = Number(e.coord.x.toFixed(6));
      const nextPosition = new naver.maps.LatLng(newLat, newLon);

      modalMarkerRef.current.setPosition(nextPosition);
      setFormData(prev => ({ ...prev, lat: newLat, lon: newLon }));
    });

    return () => {
      naver.maps.Event.removeListener(clickListener);
      modalMarkerRef.current = null;
      modalMapRef.current = null;
    };
  }, [isMapModalOpen, isNaverMapLoaded, formData.lat, formData.lon]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    ShopFormUtils.handleInputChange(e, setFormData, setErrors);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = ShopFormUtils.validateCreateForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reset form
  const handleReset = () => {
    setFormData(ShopFormUtils.getDefaultFormData());
    setErrors({});
    setSubmitError(null);
    setPrimaryUploadFiles([]);
    setPrimaryImagePreview('');
    setPrimaryImageError('');
    setRewardCardUploadFiles([]);
    setRewardCardImagePreview('');
    setRewardCardImageError('');
  };

  const handleValidatedPrimaryImage = async ({
    file,
    dataUrl,
  }: {
    file: File;
    dataUrl: string;
  }) => {
    const projectCode = projectDetail?.code || getProjectCode();
    if (!projectCode) {
      setPrimaryImageError('프로젝트 정보가 없습니다.');
      return;
    }

    setPrimaryUploadFiles([]);
    setPrimaryImagePreview('');
    setPrimaryImageError('');
    setIsPrimaryImageUploading(true);

    try {
      const { upload } = await uploadSingleFileViaPresign(file, {
        domain: 'SHOP',
        projectCode,
      });
      setPrimaryUploadFiles([toUploadFilePayload(upload)]);
      setPrimaryImagePreview(dataUrl);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : '이미지 업로드 중 오류가 발생했습니다';
      setPrimaryImageError(message);
      setPrimaryImagePreview('');
      setPrimaryUploadFiles([]);
    } finally {
      setIsPrimaryImageUploading(false);
    }
  };

  const handleValidatedRewardCardImage = async ({
    file,
    dataUrl,
  }: {
    file: File;
    dataUrl: string;
  }) => {
    const projectCode = projectDetail?.code || getProjectCode();
    if (!projectCode) {
      setRewardCardImageError('프로젝트 정보가 없습니다.');
      return;
    }

    setRewardCardUploadFiles([]);
    setRewardCardImagePreview('');
    setRewardCardImageError('');
    setIsRewardCardImageUploading(true);

    try {
      const { upload } = await uploadSingleFileViaPresign(file, {
        domain: 'SHOP',
        projectCode,
      });
      setRewardCardUploadFiles([toUploadFilePayload(upload)]);
      setRewardCardImagePreview(dataUrl);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : '이미지 업로드 중 오류가 발생했습니다';
      setRewardCardImageError(message);
      setRewardCardImagePreview('');
      setRewardCardUploadFiles([]);
    } finally {
      setIsRewardCardImageUploading(false);
    }
  };

  const handleRemovePrimaryImage = () => {
    setPrimaryUploadFiles([]);
    setPrimaryImagePreview('');
    setPrimaryImageError('');
  };

  const handleRemoveRewardCardImage = () => {
    setRewardCardUploadFiles([]);
    setRewardCardImagePreview('');
    setRewardCardImageError('');
  };

  const extractCreatedShopCode = (result: { data?: unknown }): string | undefined => {
    const data = result.data as { code?: string; data?: { code?: string } } | undefined;
    return data?.code ?? data?.data?.code;
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isPrimaryImageUploading || isRewardCardImageUploading) {
      alert('이미지 업로드가 진행 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const projectCode = getProjectCode();
      const shopData = {
        outletCode: formData.outletCode,
        status: formData.status,
        number: formData.number,
        type: formData.type,
        name: formData.name,
        brandCode: formData.brandCode || undefined,
        shortName: formData.shortName || undefined,
        salesTime: formData.salesTime || undefined,
        comment: formData.comment || undefined,
        intro: formData.intro || undefined,
        phoneNumber: formData.phoneNumber,
        ownerPhoneNumber: formData.ownerPhoneNumber || undefined,
        homepage: formData.homepage || undefined,
        postalCode: formData.postalCode || undefined,
        regionCode: formData.regionCode || undefined,
        addressRoad: formData.addressRoad,
        addressDetail: formData.addressDetail,
        lat: formData.lat,
        lon: formData.lon,
        loginPassword: formData.loginPassword || undefined,
      };

      const result = await createShop(projectCode, shopData);
      if (!result.success) {
        setSubmitError(result.message || '매장 생성에 실패했습니다.');
        return;
      }

      const shopCode = extractCreatedShopCode(result);
      const hasFileUploads = primaryUploadFiles.length > 0 || rewardCardUploadFiles.length > 0;

      if (hasFileUploads) {
        if (!shopCode) {
          alert('매장은 생성되었지만 매장 코드를 받지 못해 이미지를 등록하지 못했습니다.');
          navigate('/shops');
          return;
        }

        try {
          for (const uploadFile of primaryUploadFiles) {
            await createShopFile(projectCode, shopCode, toShopPrimaryFileRequest(uploadFile));
          }
          for (const uploadFile of rewardCardUploadFiles) {
            await createShopFile(projectCode, shopCode, toShopRewardCardFileRequest(uploadFile));
          }
        } catch (fileError) {
          console.error('매장 이미지 등록 오류:', fileError);
          alert('매장은 생성되었습니다.\n이미지 등록에 실패했습니다. 수정 화면에서 다시 등록해 주세요.');
          navigate('/shops');
          return;
        }
      }

      alert('매장이 성공적으로 생성되었습니다.');
      navigate('/shops');
    } catch (error: unknown) {
      console.error('매장 생성 오류:', error);
      const displayMessage =
        error instanceof Error
          ? error.message
          : '매장 생성 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 주소 검색
  const handleAddressSearch = () => {
    setIsAddressSearchOpen(true);
    setAddressSearchError(null);
    setAddressResults([]);
  };

  // 지도 위치 선택
  const handleMapSelection = () => {
    setIsMapModalOpen(true);
  };

  const handleSearchAddressByKeyword = () => {
    if (!isNaverMapLoaded) {
      setAddressSearchError('네이버 지도 SDK 로딩 중입니다. 잠시 후 다시 시도해 주세요.');
      return;
    }

    const keyword = addressKeyword.trim();
    if (!keyword) {
      setAddressSearchError('검색어를 입력해 주세요.');
      return;
    }

    setIsSearchingAddress(true);
    setAddressSearchError(null);

    window.naver.maps.Service.geocode({ query: keyword }, (status: any, response: any) => {
      setIsSearchingAddress(false);

      if (status !== window.naver.maps.Service.Status.OK) {
        setAddressSearchError('주소 검색에 실패했습니다.');
        setAddressResults([]);
        return;
      }

      const items = response.v2?.addresses || [];
      if (!items.length) {
        setAddressSearchError('검색 결과가 없습니다.');
        setAddressResults([]);
        return;
      }

      const mappedResults: AddressData[] = items.slice(0, 10).map((item: any) => {
        const sidoName =
          item.addressElements?.find((element: any) => element.types?.includes('SIDO'))?.longName ||
          item.addressElements?.find((element: any) => element.types?.includes('SIDO'))?.shortName ||
          item.addressElements?.[0]?.longName ||
          '';

        return {
          postalCode: item.zipCode || '',
          road: item.roadAddress || item.jibunAddress || '',
          regionCode: toTopLevelRegion(sidoName),
          lat: Number(item.y),
          lon: Number(item.x)
        };
      });

      setAddressResults(mappedResults);
    });
  };

  const handleReverseGeocode = (lat: number, lon: number) => {
    if (!isNaverMapLoaded) return;

    setIsReverseGeocoding(true);

    const coords = new window.naver.maps.Point(lon, lat);
    window.naver.maps.Service.reverseGeocode(
      {
        coords,
        orders: [
          window.naver.maps.Service.OrderType.ROAD_ADDR,
          window.naver.maps.Service.OrderType.ADDR
        ].join(',')
      },
      (status: any, response: any) => {
        setIsReverseGeocoding(false);
        if (status !== window.naver.maps.Service.Status.OK) return;

        const results = response.v2?.results || [];
        const roadResult = results.find((result: any) => result.name === 'roadaddr');
        const addrResult = results.find((result: any) => result.name === 'addr');
        const picked = roadResult || addrResult;
        if (!picked) return;

        const region = picked.region || {};
        const land = picked.land || {};
        const landNo = land.number1 ? `${land.number1}${land.number2 ? `-${land.number2}` : ''}` : '';
        // 도로명 주소는 시/도 + 시/군/구 + 도로명 + 건물번호 형태로 구성한다.
        const road = [region.area1?.name, region.area2?.name, land.name, landNo].filter(Boolean).join(' ');

        setFormData(prev => ({
          ...prev,
          addressRoad: road || prev.addressRoad,
          regionCode: toTopLevelRegion(region.area1?.name) || prev.regionCode
        }));
      }
    );
  };

  // 주소 선택
  const selectAddress = (address: AddressData) => {
    setFormData(prev => ({
      ...prev,
      postalCode: address.postalCode,
      addressRoad: address.road,
      regionCode: address.regionCode,
      lat: address.lat,
      lon: address.lon
    }));
    setIsAddressSearchOpen(false);
    setAddressKeyword(address.road);
    setAddressResults([]);
  };


  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {projectDetail && (
        <ProjectSidebar
          isDesktopSidebarOpen={isDesktopSidebarOpen}
          setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
          projectDetail={projectDetail}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        {projectDetail && (
          <Topbar
            title="매장 생성"
            subtitle={projectDetail.name}
            isMobileSidebarOpen={isMobileSidebarOpen}
            setIsMobileSidebarOpen={setIsMobileSidebarOpen}
            isProfileDropdownOpen={isProfileDropdownOpen}
            setIsProfileDropdownOpen={setIsProfileDropdownOpen}
            showMobileLogo={true}
            projectDetail={projectDetail}
          />
        )}

        {/* Create Store Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <button 
              onClick={() => navigate('/shops')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              매장 목록으로
            </button>

            {/* Error Message */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">오류가 발생했습니다</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{submitError}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Home className="w-5 h-5 mr-2" />
                  기본 정보
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Brand */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      브랜드
                    </label>
                    <select
                      name="brandCode"
                      value={formData.brandCode || ''}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.brandCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      disabled={isLoadingBrands}
                    >
                      <option value="">브랜드를 선택하세요</option>
                      {brands.map((brand) => {
                        // Non-breaking space (공백이 축약되지 않도록)
                        const nbsp = '\u00A0';
                        // depth에 따라 들여쓰기와 "ㄴ" 기호 추가
                        const indent = (nbsp + nbsp).repeat(brand.depth);
                        const prefix = brand.depth > 0 ? 'ㄴ ' : '';
                        const displayName = `${indent}${prefix}${brand.name} (${brand.code})`;
                        
                        return (
                          <option key={brand.code} value={brand.code}>
                            {displayName}
                          </option>
                        );
                      })}
                    </select>
                    {errors.brandCode && <p className="mt-1 text-xs text-red-500">{errors.brandCode}</p>}
                  </div>

                  {/* Store Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      매장 코드 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="outletCode"
                      value={formData.outletCode}
                      onChange={handleInputChange}
                      maxLength={20}
                      className={`w-full px-3 py-2 border ${errors.outletCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="SHOP000001"
                    />
                    {errors.outletCode && <p className="mt-1 text-xs text-red-500">{errors.outletCode}</p>}
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상태 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="ACTIVE">활성</option>
                      <option value="INACTIVE">비활성</option>
                      <option value="PENDING">대기중</option>
                      <option value="TERMINATED">종료</option>
                      <option value="DELETED">삭제</option>
                    </select>
                  </div>

                  {/* Store Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      매장 번호 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="number"
                      value={formData.number || ''}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.number ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                      placeholder="1"
                    />
                    {errors.number && <p className="mt-1 text-xs text-red-500">{errors.number}</p>}
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      타입 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="franchise">가맹점</option>
                      <option value="company">직영점</option>
                      <option value="popup">팝업</option>
                      <option value="shopping_mall">쇼핑몰</option>
                    </select>
                  </div>

                  {/* Store Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      매장명 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={30}
                      className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="강남 플래그십 스토어"
                    />
                    {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                  </div>

                  {/* Short Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      축약명
                    </label>
                    <input
                      type="text"
                      name="shortName"
                      value={formData.shortName}
                      onChange={handleInputChange}
                      maxLength={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="강남점"
                    />
                  </div>

                  {/* Sales Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      영업시간
                    </label>
                    <input
                      type="text"
                      name="salesTime"
                      value={formData.salesTime}
                      onChange={handleInputChange}
                      maxLength={200}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="평일 08:00-22:00, 주말 10:00-21:00"
                    />
                  </div>
                </div>

                {/* Comment */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    짧은 소개
                  </label>
                  <input
                    type="text"
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                    maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="강남역 1번 출구 바로 앞"
                  />
                </div>

                {/* Intro */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    상세 소개
                  </label>
                  <textarea
                    name="intro"
                    value={formData.intro}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="매장에 대한 상세한 소개를 입력하세요..."
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  연락처 정보
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Store Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      매장 전화번호 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      maxLength={20}
                      className={`w-full px-3 py-2 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="0212345678"
                    />
                    {errors.phoneNumber && <p className="mt-1 text-xs text-red-500">{errors.phoneNumber}</p>}
                  </div>

                  {/* Owner Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      점주 연락처
                    </label>
                    <input
                      type="tel"
                      name="ownerPhoneNumber"
                      value={formData.ownerPhoneNumber}
                      onChange={handleInputChange}
                      maxLength={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="01012345678"
                    />
                  </div>

                  {/* Homepage */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      홈페이지
                    </label>
                    <input
                      type="text"
                      name="homepage"
                      value={formData.homepage}
                      onChange={handleInputChange}
                      maxLength={250}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://store.example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  위치 정보
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Postal Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      우편번호
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                      name="postalCode"
                      value={formData.postalCode}
                        onChange={handleInputChange}
                        maxLength={10}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="06234"
                      />
                      <button
                        type="button"
                        onClick={handleAddressSearch}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                      >
                        주소 검색
                      </button>
                    </div>
                  </div>

                  {/* Region Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      지역 코드
                    </label>
                    <input
                      type="text"
                      name="regionCode"
                      value={formData.regionCode}
                      onChange={handleInputChange}
                      maxLength={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="서울"
                    />
                  </div>

                  {/* Road Address */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      도로명 주소 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="addressRoad"
                      value={formData.addressRoad}
                      onChange={handleInputChange}
                      maxLength={200}
                      className={`w-full px-3 py-2 border ${errors.addressRoad ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="서울특별시 강남구 테헤란로 123"
                    />
                    {errors.addressRoad && <p className="mt-1 text-xs text-red-500">{errors.addressRoad}</p>}
                  </div>

                  {/* Detail Address */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상세 주소 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="addressDetail"
                      value={formData.addressDetail}
                      onChange={handleInputChange}
                      maxLength={200}
                      className={`w-full px-3 py-2 border ${errors.addressDetail ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="강남빌딩 1층"
                    />
                    {errors.addressDetail && <p className="mt-1 text-xs text-red-500">{errors.addressDetail}</p>}
                  </div>

                  {/* Coordinates */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      위도 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="lat"
                      value={formData.lat}
                      onChange={handleInputChange}
                      step="0.000001"
                      className={`w-full px-3 py-2 border ${errors.location ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="37.498095"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      경도 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="lon"
                      value={formData.lon}
                      onChange={handleInputChange}
                      step="0.000001"
                      className={`w-full px-3 py-2 border ${errors.location ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="127.027610"
                    />
                  </div>

                  {errors.location && (
                    <div className="md:col-span-2">
                      <p className="text-xs text-red-500">{errors.location}</p>
                    </div>
                  )}

                  {/* Map Button */}
                  <div className="md:col-span-2">
                    <button
                      type="button"
                      onClick={handleMapSelection}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center"
                    >
                      <MapIcon className="w-5 h-5 mr-2" />
                      지도에서 위치 선택
                    </button>
                  </div>

                  {/* Map Preview Area */}
                  <div className="md:col-span-2">
                    {!import.meta.env.VITE_NAVER_MAP_CLIENT_ID ? (
                      <div className="h-64 bg-yellow-50 rounded-lg flex items-center justify-center border border-yellow-300">
                        <p className="text-sm text-yellow-700">
                          `.env`에 `VITE_NAVER_MAP_CLIENT_ID`를 설정하면 지도가 표시됩니다.
                        </p>
                      </div>
                    ) : (
                      <div ref={mapPreviewRef} className="h-64 rounded-lg border border-gray-300" />
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Image className="w-5 h-5 mr-2" />
                  이미지
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ShopImageUploadPreview
                    variant="square"
                    label="대표 이미지"
                    hint="권장 비율은 1:1입니다. 다른 비율도 업로드할 수 있으며, 이후 크롭됩니다."
                    imageUrl={primaryImagePreview || null}
                    isUploading={isPrimaryImageUploading}
                    error={primaryImageError}
                    onValidatedImage={handleValidatedPrimaryImage}
                    onRemoveImage={handleRemovePrimaryImage}
                    onImageError={setPrimaryImageError}
                    onImageLoadError={handleRemovePrimaryImage}
                  />
                  <ShopImageUploadPreview
                    variant="reward-card"
                    label="리워드 카드"
                    hint="권장 비율은 16:9입니다. 다른 비율도 업로드할 수 있으며, 이후 크롭됩니다."
                    imageUrl={rewardCardImagePreview || null}
                    isUploading={isRewardCardImageUploading}
                    error={rewardCardImageError}
                    onValidatedImage={handleValidatedRewardCardImage}
                    onRemoveImage={handleRemoveRewardCardImage}
                    onImageError={setRewardCardImageError}
                    onImageLoadError={handleRemoveRewardCardImage}
                  />
                </div>
              </div>

              {/* Login Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Lock className="w-5 h-5 mr-2" />
                  로그인 정보
                </h2>
                
                <div className="max-w-md">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    로그인 비밀번호 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="loginPassword"
                      value={formData.loginPassword}
                      onChange={handleInputChange}
                      maxLength={20}
                      className={`w-full px-3 py-2 pr-10 border ${errors.loginPassword ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="매장 관리자 로그인 비밀번호"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5 text-gray-400" />
                      ) : (
                        <Eye className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.loginPassword && <p className="mt-1 text-xs text-red-500">{errors.loginPassword}</p>}
                  <p className="mt-2 text-xs text-gray-500">
                    매장 관리자가 시스템에 로그인할 때 사용할 비밀번호입니다.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  초기화
                </button>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => navigate('/shops')}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || isPrimaryImageUploading || isRewardCardImageUploading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {isSubmitting ? '생성 중...' : '매장 생성'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>

      {/* Address Search Modal */}
      {isAddressSearchOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">주소 검색</h3>
              <button
                onClick={() => setIsAddressSearchOpen(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={addressKeyword}
                  onChange={(e) => setAddressKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchAddressByKeyword();
                    }
                  }}
                  placeholder="도로명 또는 지번 주소 검색"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleSearchAddressByKeyword}
                  disabled={isSearchingAddress}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                >
                  {isSearchingAddress ? '검색 중...' : '검색'}
                </button>
              </div>
              
              {addressSearchError && (
                <p className="mb-3 text-sm text-red-600">{addressSearchError}</p>
              )}

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {addressResults.map((result, idx) => (
                  <div
                    key={`${result.road}-${idx}`}
                    className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => selectAddress(result)}
                  >
                    <p className="font-medium">{result.road}</p>
                    <p className="text-sm text-gray-500">
                      우편번호: {result.postalCode || '-'} / 위도: {result.lat}, 경도: {result.lon}
                    </p>
                  </div>
                ))}
              </div>
              
              <p className="mt-4 text-xs text-gray-500">
                * 네이버 지오코딩 API 검색 결과입니다.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Map Modal */}
      {isMapModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">지도에서 위치 선택</h3>
              <button
                onClick={() => setIsMapModalOpen(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {!import.meta.env.VITE_NAVER_MAP_CLIENT_ID ? (
                <div className="h-96 bg-yellow-50 rounded-lg flex items-center justify-center border border-yellow-300">
                  <p className="text-sm text-yellow-700">
                    `.env`에 `VITE_NAVER_MAP_CLIENT_ID`를 설정해 주세요.
                  </p>
                </div>
              ) : (
                <div ref={mapModalRef} className="h-96 rounded-lg border border-gray-300" />
              )}
              
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>사용 방법:</strong>
                </p>
                <ul className="mt-2 text-sm text-gray-600 space-y-1">
                  <li>• 지도를 클릭하여 위치를 선택하세요</li>
                  <li>• 마우스 휠로 확대/축소가 가능합니다</li>
                  <li>• 선택한 위치는 자동으로 위도/경도가 입력됩니다</li>
                </ul>
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    현재 좌표: {formData.lat || '-'}, {formData.lon || '-'}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (formData.lat && formData.lon) {
                        handleReverseGeocode(formData.lat, formData.lon);
                      }
                    }}
                    disabled={!formData.lat || !formData.lon || isReverseGeocoding}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    {isReverseGeocoding ? '주소 변환 중...' : '좌표로 주소 채우기'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreCreate;